import { Form, Row, Col, Button, InputNumber } from 'antd';
import styles from './DLCFile.module.less';
import FormItem from 'components/form/FormItem';
import api, { DLCAssetsType } from 'api';
import { FormProvider, useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FileModel, FileYupSchema, GetUploadLink, ValidateS3Keys, generateUploadFileList } from 'utils/fileUtils';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FileUploadDragger from 'components/FileUploadDragger';
import { projectSelector } from 'redux/selectors/projectSelector';
import OmniNotification from 'components/OmniNotification';
import { isEmpty } from 'lodash';
import { DLCTabName, IDownloadableContentDetailDto } from './EditDownloadableContent';
import { RcFile } from 'rc-upload/lib/interface';
import { mapErrorToErrorMessage } from 'utils/errorUtils';

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		dlcFiles: yup
			.array()
			.of(yup.object(FileYupSchema))
			.test('ValidateS3Keys', '', (value) => {
				if (!value || value.length === 0) {
					return true;
				}
				return ValidateS3Keys(value);
			}),
		minimumCompatibleVerison: yup.number().when('dlcFiles', {
			is: (dlcFiles) => dlcFiles && dlcFiles.length > 0,
			then: yup
				.number()
				.nullable()
				.required(
					t('validation.required', {
						fieldName: t('downloadableContentEdit.tabs.dlcFile.form.minimumCompatibleVerison.title'),
					}),
				)
				.min(
					1,
					t('validation.positive', {
						fieldName: t('downloadableContentEdit.tabs.dlcFile.form.minimumCompatibleVerison.title'),
					}),
				),
			otherwise: yup.number().nullable(),
		}),
	});

interface FormValues {
	dlcFiles?: FileModel[];
	minimumCompatibleVerison: number;
}

type Props = {
	id: number;
	data: IDownloadableContentDetailDto;
	onSave: (number: string) => void;
};

export interface DLCFileRef {
	validateDLCFileTab: () => void;
}

export default forwardRef(function DLCFile(props: Props, ref: any) {
	const { t } = useTranslation();
	const projectInfo = useSelector(projectSelector);
	const { id, data, onSave } = props;
	const [defaultFiles, setDefaultFiles] = useState<FileModel[]>([]);
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const [timesOfLoadData, setTimesOfLoadData] = useState<number>(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const methods = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues: {
			dlcFiles: undefined,
		},
	});

	const { control, handleSubmit, reset, trigger, watch, formState } = methods;

	useImperativeHandle(ref, () => ({
		async validateDLCFileTab() {
			return await trigger();
		},
	}));

	const watchedDlcFIles = watch('dlcFiles');

	useEffect(() => {
		if (!isEmpty(data) && (!data.tabName || data.tabName == DLCTabName.DlcFile)) {
			setTimesOfLoadData(timesOfLoadData + 1);
		}
		if (data && data.assets && data.assets!.length > 0 && (isSubmitting || timesOfLoadData == 0)) {
			var files =
				data.assets
					?.filter((item) => item.type == DLCAssetsType.File)
					.map(
						(item) =>
							({
								s3Key: item.key,
								fileName: item.name,
							}) as FileModel,
					) ?? [];
			setDefaultFiles(files);
			reset({
				dlcFiles: files,
				minimumCompatibleVerison: data.minimumCompatibleVerison,
			});
		}
	}, [data]);

	const submitEvent = () => {
		handleSubmit(async (values: FormValues) => {
			setIsSubmitting(true);

			const assetItems =
				values.dlcFiles?.map((file) => ({
					name: file.fileName,
					key: file.s3Key,
					type: DLCAssetsType.File,
					dlcId: id,
				})) ?? [];

			api
				.updateFilesForDLC(id, {
					productId: projectInfo?.productId,
					assetItems: assetItems,
					minimumCompatibleVerison: values.minimumCompatibleVerison,
				})
				.then(() => {
					OmniNotification.success({
						message: t('downloadableContentEdit.message.update.success.title'),
						description: t('downloadableContentEdit.message.update.success.description'),
					});
					onSave(DLCTabName.DlcFile);
					setIsDirty(false);
					reset(values);
				})
				.catch((error) => {
					OmniNotification.error({
						message: t('notification.error.title'),
						description: mapErrorToErrorMessage(error) || t('api.defaultError'),
					});
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		})();
	};

	return (
		<>
			<FormProvider {...methods}>
				<Form data-testid="form" layout="vertical" onFinish={submitEvent}>
					<Row>
						<div style={{ flexDirection: 'row', alignItems: 'center' }}>
							<span className={styles.title}>{t('downloadableContentEdit.tabs.dlcFile.title')}</span>
							<span className={styles.description}>{t('downloadableContentEdit.tabs.dlcFile.description')}</span>
							<span>{isDirty ? 'true' : 'false'}</span>
						</div>
					</Row>
					<Row
						style={{
							marginTop: 24,
						}}
						gutter={[24, 24]}
					>
						<Col span={12}>
							<FormItem
								label={t('downloadableContentEdit.tabs.dlcFile.form.uploadDLCFile.title')}
								name="dlcFiles"
								control={control}
								hideError={true}
								render={({ field, fieldState }) => (
									<FileUploadDragger
										maxCount={256}
										multiple
										error={fieldState.error}
										data-testid="file-uploader"
										hint={t('downloadableContentEdit.tabs.dlcFile.form.uploadDLCFile.description')}
										acceptedFileSize={4096}
										uploadLinkFunction={GetUploadLink}
										customSuccessCallback={() => {
											OmniNotification.success({
												message: t('downloadableContentEdit.message.upload.success.title'),
												description: t('downloadableContentEdit.message.upload.success.description'),
											});
										}}
										field={field as any}
										defaultFileList={generateUploadFileList(defaultFiles)}
										onFileListChange={(files) => {
											setIsDirty(true);
											setDefaultFiles(files);
											reset(
												{
													dlcFiles: files,
												},
												{
													keepDirty: true,
												},
											);
										}}
										beforeUploadValidation={async (file: RcFile) => {
											try {
												await api.validateDlcFile(
													id,
													projectInfo?.productId!,
													file.name,
													defaultFiles.map((f) => f.fileName),
												);
												return { success: true };
											} catch (error) {
												return {
													success: false,
													errorMessage: t('downloadableContentEdit.tabs.dlcFile.form.uploadDLCFile.unique', {
														fileName: file.name,
													}),
												};
											}
										}}
									/>
								)}
							/>
						</Col>
					</Row>
					{watchedDlcFIles?.length ? (
						<Row
							style={{
								marginBottom: 24,
							}}
							gutter={[24, 24]}
						>
							<Col span={12}>
								<FormItem
									name="minimumCompatibleVerison"
									label={t('downloadableContentEdit.tabs.dlcFile.form.minimumCompatibleVerison.title')}
									control={control}
									render={({ field }) => (
										<InputNumber
											data-testid="test-minimum-compatible-version-input"
											style={{ width: '100%' }}
											{...field}
											controls={false}
											precision={0}
											size="large"
										/>
									)}
								/>
							</Col>
						</Row>
					) : null}
					<Row>
						<span>{t('downloadableContentEdit.tabs.dlcFile.form.uploadDLCFile.contactUs')}</span>
						<a href="mailto:dev-rel@virtuix.com" className={styles.footerLinkWhite}>
							&nbsp;dev-rel@virtuix.com
						</a>
					</Row>
					<Button
						data-testid="dlc-file-tab-save-btn"
						loading={isSubmitting}
						type="primary"
						htmlType="submit"
						size="large"
						disabled={timesOfLoadData > 1 && (!(isDirty || formState.isDirty) || isSubmitting)}
						style={{ marginTop: 24 }}
					>
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
});
